import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LabelExportFormat, LabelFormat } from "generated-graphql/graphql";
import { ReactElement } from "react";

import { FileTypeToggle, FormatRadioGroup } from "./LabelFormatControls";
import { LABEL_SIZE_OPTIONS, LabelSizeOption } from "./labelFormState";
import { CardTitle, Eyebrow } from "./LabelScreenTypography";

/** File type + the Download button -- grouped together because both serve
 * the "get a file to keep" intent, distinct from the Print cluster below. */
function DownloadCluster({
  fileType,
  onFileTypeChange,
  onExport,
  exporting,
}: {
  fileType: LabelExportFormat;
  onFileTypeChange: (fileType: LabelExportFormat) => void;
  onExport: () => void;
  exporting: boolean;
}): ReactElement {
  return (
    <Box sx={{ mt: 3 }}>
      <Eyebrow mt={0}>File type</Eyebrow>

      {/* Not showing the Encamp-logo switch for now -- re-enable if anyone
          asks to hide the Encamp logo in the QR. `branded` still defaults
          true and is still sent on export. */}

      <Stack direction="row" spacing={1.5} alignItems="stretch" sx={{ mt: 1 }}>
        <Box sx={{ flex: 1 }}>
          <FileTypeToggle value={fileType} onChange={onFileTypeChange} />
        </Box>
        <Button variant="outlined" sx={{ flex: 1 }} onClick={onExport} loading={exporting}>
          Download
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        Downloads always render the 4 × 6 label.
      </Typography>
    </Box>
  );
}

/** Print size + the Print button -- the page's primary CTA, kept visually
 * separate from the Download cluster above (different intent: send to a
 * printer now, vs. save a file). */
function PrintCluster({
  size,
  onSizeChange,
  onPrint,
}: {
  size: LabelSizeOption;
  onSizeChange: (size: LabelSizeOption) => void;
  onPrint: () => void;
}): ReactElement {
  return (
    <Box sx={{ mt: 3 }}>
      <Eyebrow mt={0}>Print size</Eyebrow>
      <Stack direction="row" spacing={1.5} alignItems="stretch" sx={{ mt: 1 }}>
        <Select<LabelSizeOption>
          value={size}
          onChange={(event: SelectChangeEvent<LabelSizeOption>) => onSizeChange(event.target.value)}
          size="small"
          sx={{ flex: 1 }}
        >
          {LABEL_SIZE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        {/* Sibling screens' brand-orange CTAs use color="secondary" -- the
            theme's `primary` is Sapphire teal, not the Encamp orange (see
            apps/oak/src/theme.tsx and e.g. Waste/Submissions/index.tsx's
            "Add submission" button). */}
        <Button
          variant="contained"
          color="secondary"
          sx={{ flex: 1 }}
          startIcon={<PrintIcon />}
          onClick={onPrint}
        >
          Print
        </Button>
      </Stack>
    </Box>
  );
}

export function PrintFormatCard({
  layout,
  onLayoutChange,
  fileType,
  onFileTypeChange,
  size,
  onSizeChange,
  note,
  onNoteChange,
  onNoteBlur,
  onPrint,
  onExport,
  exporting,
}: {
  layout: LabelFormat;
  onLayoutChange: (layout: LabelFormat) => void;
  fileType: LabelExportFormat;
  onFileTypeChange: (fileType: LabelExportFormat) => void;
  size: LabelSizeOption;
  onSizeChange: (size: LabelSizeOption) => void;
  note: string;
  onNoteChange: (note: string) => void;
  onNoteBlur: () => void;
  onPrint: () => void;
  onExport: () => void;
  exporting: boolean;
}): ReactElement {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <CardTitle>Print &amp; format</CardTitle>

      <Eyebrow mt={0}>Print format</Eyebrow>
      <FormatRadioGroup layout={layout} onChange={onLayoutChange} />

      <TextField
        label="Label note"
        multiline
        minRows={2}
        fullWidth
        value={note}
        onChange={(event) => onNoteChange(event.target.value)}
        onBlur={onNoteBlur}
        helperText="Prints on the label only"
        sx={{ mt: 3 }}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", fontStyle: "italic", mt: 2 }}
      >
        Preview updates live as you change these settings.
      </Typography>

      <DownloadCluster
        fileType={fileType}
        onFileTypeChange={onFileTypeChange}
        onExport={onExport}
        exporting={exporting}
      />

      <Divider sx={{ my: 3 }} />

      <PrintCluster size={size} onSizeChange={onSizeChange} onPrint={onPrint} />
    </Paper>
  );
}
